import {
    getAxiosInstance,
} from '@moeinmdn/mixitweb-front-modules/src/plugin/utils/setInstance.js'
import {
    countUnreadTickets,
    isTicketUnread,
    markTicketSeen,
} from '@/plugin/support/helpdeskSeen'

let statusPromise = null
let ticketsPromise = null

export default {
    namespaced: true,
    state: {
        enabled: null,
        ready: false,
        missing: [],
        tickets: [],
        activeTicket: null,
        loading: false,
        detailLoading: false,
        saving: false,
        seenVersion: 0,
        statusLoaded: false,
        ticketsLoaded: false,
    },
    mutations: {
        SET_STATUS(state, payload) {
            state.enabled = payload.enabled !== false
            state.ready = Boolean(payload.ready)
            state.missing = Array.isArray(payload.missing) ? payload.missing : []
            state.statusLoaded = true
        },
        SET_TICKETS(state, tickets) {
            state.tickets = tickets || []
            state.ticketsLoaded = true
        },
        SET_ACTIVE_TICKET(state, ticket) {
            state.activeTicket = ticket || null
        },
        SET_LOADING(state, value) {
            state.loading = value
        },
        SET_DETAIL_LOADING(state, value) {
            state.detailLoading = value
        },
        SET_SAVING(state, value) {
            state.saving = value
        },
        PREPEND_TICKET(state, ticket) {
            state.tickets = [ticket, ...state.tickets]
        },
        BUMP_SEEN(state) {
            state.seenVersion += 1
        },
        MARK_TICKET_READ_LOCAL(state, ticketId) {
            const id = Number(ticketId)
            state.tickets = state.tickets.map((ticket) => {
                if (Number(ticket.id) !== id) {
                    return ticket
                }
                return {...ticket, _seen_local: true}
            })
        },
    },
    actions: {
        fetchStatus({commit, state}, options = {}) {
            const force = Boolean(options && options.force)
            if (!force && state.statusLoaded) {
                return Promise.resolve({
                    enabled: state.enabled,
                    ready: state.ready,
                    missing: state.missing,
                })
            }
            if (!force && statusPromise) {
                return statusPromise
            }
            const axios = getAxiosInstance()
            statusPromise = axios.get('helpdesk/status').then((res) => {
                const data = (res.data && res.data.data) || {}
                commit('SET_STATUS', data)
                return data
            }).catch(() => {
                const fallback = {
                    enabled: process.env.VUE_APP_ODOO_HELPDESK_ENABLED !== 'false',
                    ready: false,
                    missing: [],
                }
                commit('SET_STATUS', fallback)
                return fallback
            }).finally(() => {
                statusPromise = null
            })
            return statusPromise
        },
        fetchTickets({commit, state, rootGetters}, options = {}) {
            const force = Boolean(options && options.force)
            const profile = rootGetters['User/getProfile'] || {}
            if (!profile.id) {
                return Promise.resolve([])
            }
            if (!force && state.ticketsLoaded && !state.loading) {
                return Promise.resolve(state.tickets)
            }
            if (!force && ticketsPromise) {
                return ticketsPromise
            }
            commit('SET_LOADING', true)
            const axios = getAxiosInstance()
            ticketsPromise = axios.get(`helpdesk/user/${profile.id}/tickets`).then((res) => {
                const tickets = (res.data && res.data.data && res.data.data.tickets) || []
                commit('SET_TICKETS', tickets)
                return tickets
            }).finally(() => {
                commit('SET_LOADING', false)
                ticketsPromise = null
            })
            return ticketsPromise
        },
        fetchTicketDetail({commit, dispatch, rootGetters}, ticketId) {
            const profile = rootGetters['User/getProfile'] || {}
            if (!profile.id || !ticketId) {
                return Promise.resolve(null)
            }
            commit('SET_DETAIL_LOADING', true)
            const axios = getAxiosInstance()
            return axios.get(`helpdesk/user/${profile.id}/tickets/${ticketId}`, {
                params: {_ts: Date.now()},
            }).then((res) => {
                const ticket = (res.data && res.data.data) || null
                commit('SET_ACTIVE_TICKET', ticket)
                if (ticket && ticket.id) {
                    dispatch('markTicketRead', ticket)
                }
                return ticket
            }).finally(() => {
                commit('SET_DETAIL_LOADING', false)
            })
        },
        markTicketRead({commit, rootGetters}, ticket) {
            const profile = rootGetters['User/getProfile'] || {}
            if (!ticket || !ticket.id) {
                return
            }
            const seenAt = ticket.last_reply_at
                || new Date().toISOString().slice(0, 19).replace('T', ' ')
            markTicketSeen(profile.id, ticket.id, seenAt)
            commit('MARK_TICKET_READ_LOCAL', ticket.id)
            commit('BUMP_SEEN')
        },
        clearActiveTicket({commit}) {
            commit('SET_ACTIVE_TICKET', null)
        },
        createTicket({commit, rootGetters}, form) {
            const profile = rootGetters['User/getProfile'] || {}
            commit('SET_SAVING', true)
            const axios = getAxiosInstance()
            return axios.post(`helpdesk/user/${profile.id}/tickets`, form).then((res) => {
                const ticket = (res.data && res.data.data) || {}
                if (ticket.id) {
                    commit('PREPEND_TICKET', ticket)
                }
                return res
            }).finally(() => {
                commit('SET_SAVING', false)
            })
        },
        replyTicket({commit, rootGetters}, {ticketId, body, captcha_id, captcha_answer, images}) {
            const profile = rootGetters['User/getProfile'] || {}
            if (!profile.id || !ticketId) {
                return Promise.reject(new Error('ticket missing'))
            }
            commit('SET_SAVING', true)
            const axios = getAxiosInstance()
            const formData = new FormData()
            if (body) {
                formData.append('body', body)
            } else {
                formData.append('body', '')
            }
            if (captcha_id) {
                formData.append('captcha_id', captcha_id)
            }
            if (captcha_answer) {
                formData.append('captcha_answer', captcha_answer)
            }
            const files = Array.isArray(images) ? images : []
            files.forEach((file) => {
                if (file) {
                    formData.append('images[]', file)
                }
            })
            return axios.post(
                `helpdesk/user/${profile.id}/tickets/${ticketId}/messages`,
                formData
            ).then((res) => {
                const data = (res.data && res.data.data) || {}
                const ticket = data.ticket || null
                if (ticket && ticket.id) {
                    commit('SET_ACTIVE_TICKET', ticket)
                }
                return data
            }).finally(() => {
                commit('SET_SAVING', false)
            })
        },
    },
    getters: {
        isEnabled: (state) => state.enabled !== false,
        isReady: (state) => {
            if (state.ready) {
                return true
            }
            if (state.enabled === false) {
                return false
            }
            const missing = state.missing || []
            if (!missing.length) {
                return false
            }
            return missing.every((item) => /project_id|odoo_id|پروژه/i.test(String(item || '')))
        },
        missing: (state) => state.missing || [],
        tickets: (state) => state.tickets,
        activeTicket: (state) => state.activeTicket,
        loading: (state) => state.loading,
        detailLoading: (state) => state.detailLoading,
        saving: (state) => state.saving,
        unreadCount: (state, getters, rootState, rootGetters) => {
            void state.seenVersion
            const profile = rootGetters['User/getProfile'] || {}
            return countUnreadTickets(state.tickets, profile.id)
        },
        isUnread: (state, getters, rootState, rootGetters) => (ticket) => {
            void state.seenVersion
            if (ticket && ticket._seen_local) {
                return false
            }
            const profile = rootGetters['User/getProfile'] || {}
            return isTicketUnread(ticket, profile.id)
        },
    },
}
